import { LitElement, html, nothing } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';

import { dispatchEvent } from '@fil-global/shared';
import '@fil-global/gds-streamline-icons/webcomponent/fil-icon-close';

import { selectorTabbable, tryFocusElems, PRECEDING, FOLLOWING } from './util.js';

import componentStyles from './fil-modal.styles.js';

/**
 * @tag fil-modal
 * @property {boolean} [active] Show/hide the component
 * @property {boolean} [noCloseIcon=false]
 * @property {boolean} [noCloseOnBackdrop=false]
 * @property {boolean} [noCloseOnEsc=false]
 * @property {"large"|"small"} [size="large"] Size of the component
 * @property {number} [zIndex] z index of the component
 * @fires close.gds.modal (event: Event) => void
 */
export class FilModal extends LitElement {
  static properties = {
    active: { type: Boolean },
    // control whether show close icon
    noCloseIcon: { type: Boolean },
    noCloseOnBackdrop: { type: Boolean },
    noCloseOnEsc: { type: Boolean },
    size: { type: String },
    zIndex: { type: Number },
  };

  static styles = [componentStyles];

  constructor() {
    super();
    this.active = false;
    this.noCloseIcon = false;
    this.noCloseOnBackdrop = false;
    this.noCloseOnEsc = false;
    this.size = 'large';
    this.zIndex = 50;

    this.handleClose = this.handleClose.bind(this);
    this.handleEsc = this.handleEsc.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.handleEsc);
    document.addEventListener('click', this.handleClickOutside);
    this.addEventListener('focusout', e => this.handleFocusOut(e));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.handleEsc);
    document.removeEventListener('click', this.handleClickOutside);
    this.removeEventListener('focusout', this.handleFocusOut);
    super.disconnectedCallback();
  }

  static _delay(ms) {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  }

  handleEsc(event) {
    const { noCloseOnEsc } = this;

    if ((event.key === 'Escape' || event.key === 'Esc') && !noCloseOnEsc) {
      this.handleClose(event);
    }
  }

  async handleFocusOut(e) {
    const { active } = this;
    const startSentinelNode = this.shadowRoot.querySelector('#start-sentinel');
    const endSentinelNode = this.shadowRoot.querySelector('#end-sentinel');
    // target: The EventTarget losing focus. relatedTarget: The EventTarget receiving focus
    const { target, relatedTarget } = e;
    const oldContains = target !== this && this.contains(target);
    // whether the target in fil-modal(this);
    const currentContains =
      relatedTarget !== this &&
      (this.contains(relatedTarget) ||
        (this.shadowRoot.contains(relatedTarget) && relatedTarget !== endSentinelNode));

    if (active && relatedTarget && oldContains && !currentContains) {
      const comparisonResult = target.compareDocumentPosition(relatedTarget);

      if (relatedTarget === startSentinelNode || (comparisonResult && PRECEDING)) {
        await this.constructor._delay();
        if (!tryFocusElems(this.shadowRoot.querySelectorAll(selectorTabbable), true)) {
          this.focus();
        }
      } else if (relatedTarget === endSentinelNode || (comparisonResult && FOLLOWING)) {
        await this.constructor._delay();
        if (!tryFocusElems(this.shadowRoot.querySelectorAll(selectorTabbable))) {
          this.focus();
        }
      }
    }
  }

  handleClickOutside(e) {
    const { noCloseOnBackdrop } = this;
    const reveal = this.shadowRoot.querySelector('.fil-reveal');
    const revealOverlay = this.shadowRoot.querySelector('.fil-reveal-overlay');
    const path = e.path || (e.composedPath && e.composedPath());
    if (this.active && !path.includes(reveal) && path.includes(revealOverlay)) {
      if (!noCloseOnBackdrop) {
        this.handleClose(e);
      }
    }
  }

  handleClose(e) {
    e.preventDefault();
    this.active = false;
    dispatchEvent(this, 'close.gds.modal', {
      value: false,
    });
  }

  handleImage(e) {
    // tell heading and footer(if it has) to update the alignment
    e.stopPropagation();
    const { value, name } = e.detail;
    const hasImage = value !== '';
    const heading = this.querySelector('fil-modal-heading');
    const footer = this.querySelector('fil-modal-footer');

    // has content image
    if (heading && name === 'content') {
      heading.setAttribute('alignment', hasImage ? 'center' : '');
    }

    // Update footer alignment if it exists
    if (footer) {
      const alignment =
        (heading && heading.getAttribute('alignment') === 'center') || hasImage ? 'center' : '';
      if (alignment) {
        footer.setAttribute('alignment', alignment);
      } else {
        footer.removeAttribute('alignment');
      }
    }
  }

  render() {
    const { active, zIndex, size, noCloseIcon } = this;
    const displayStyle = {
      display: active ? 'block' : 'none',
    };

    return html`<div
      class="fil-reveal-overlay"
      style=${styleMap({ ...displayStyle, zIndex })}
      @has-image.gds.modal=${this.handleImage}
    >
      <a
        id="start-sentinel"
        class="fil-reveal--visually-hidden"
        href="javascript:void 0"
        aria-label="navigation start in dialog"
      ></a>
      <div
        tabindex="-1"
        aria-modal="true"
        aria-hidden="${active ? 'false' : 'true'}"
        role="dialog"
        title="dialog"
        class="fil-reveal ${classMap({
          [`${size}`]: size,
          'small--no-close': noCloseIcon && size === 'small',
        })}"
        style="${styleMap({ ...displayStyle, zIndex: zIndex + 1 })}"
      >
        ${this.renderCloseIcon()}
        <slot></slot>
      </div>
      <a
        id="end-sentinel"
        class="fil-reveal--visually-hidden"
        href="javascript:void 0"
        aria-label="navigation end in dialog"
      ></a>
    </div>`;
  }

  renderCloseIcon() {
    const { noCloseIcon, size, handleClose } = this;

    if (!noCloseIcon) {
      return html`<button
        class="fil-modal__close-button${classMap({
          'fil-modal__close-button--small': size !== 'large',
        })}"
        aria-label="Close modal"
        type="button"
        tabindex="0"
        @click=${handleClose}
        @mousedown=${e => e.preventDefault()}
      >
        <fil-icon-close tabindex="-1" size="16"></fil-icon-close>
      </button>`;
    }
    return nothing;
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      if (propName === 'active') {
        if (this.active) {
          document.body.style.overflow = 'hidden';
          const startedFocus = this.shadowRoot.querySelector('#start-sentinel');
          startedFocus.focus();
        } else {
          document.body.style.overflow = '';
        }
      }
    });
  }

  disableScroll(top) {
    const htmlTag = document.querySelector('html');
    const scrollTop = top || window.scrollY;
    if (document.body.scrollHeight > window.innerHeight) {
      htmlTag.setAttribute('style', `top: ${-scrollTop}px`);
    }
    htmlTag.classList.add('is-reveal-open', 'fil-has-scroll');
  }

  enableScroll(top) {
    const htmlTag = document.querySelector('html');
    htmlTag.classList.remove('is-reveal-open', 'fil-has-scroll');
    const scrollTop = top || htmlTag.style.top;
    if (document.body.scrollHeight > window.innerHeight) {
      htmlTag.setAttribute('style', '');
      const scrollNum = -parseInt(scrollTop, 10);
      window.scrollTo(0, scrollNum);
    }
  }
}

export default FilModal;
